import tw, { styled } from "twin.macro";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Card from "app/components/ui/Card";
import Text from "app/styles/Text";
import TextField from "app/components/ui/TextField";
import Button from "app/components/ui/Button";
import TextAreaField from "app/components/ui/TextAreaField";
import { ConnectPaymentMethod } from "app/models/paymentMethod";
import usePaymentAccount from "app/hooks/usePaymentAccount";

import { getSchema } from "./utils";
import { GenericResponse } from "app/api/types";
import EnableTwoStepAuthModal from "../EnableTwoStepAuthModal";
import { useState } from "react";
import { ReactComponent as Plus } from "app/assets/icons/green-plus.svg";

interface Props {
  accountType: ConnectPaymentMethod;
  onSuccess: (d: GenericResponse<{ token: string; message: string }>) => void;
}

export default function AddUSDAccount(props: Props) {
  const { accountType, onSuccess } = props;

  const { isInitializing, initialize } = usePaymentAccount();
  const [addIntermediary, setAddIntermediary] = useState<boolean>(false);

  const schema = getSchema({
    accountType,
    withIntermediary: addIntermediary,
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const isPersonalAccount = accountType.type === "personal";

  const onSubmit = handleSubmit((data) => {
    const fields = Object.entries(data)
      .filter(([_, value]) => value !== "")
      .map(([key, value]) => ({ name: key, value }));

    initialize(
      {
        payment_method: accountType.id,
        fields,
      },
      {
        onSuccess: (data) => {
          onSuccess(data.data);
        },
      }
    );
  });

  return (
    <form onSubmit={onSubmit} tw="mt-[24px]">
      <Text tw="text-[28px]" semiBold>
        Add new bank account
      </Text>
      <Card tw="mt-[10px] p-[16px] md:p-[20px]">
        <AccountDetails>
          <TextField
            label="Account number or IBAN"
            placeholder="Enter your account number or IBAN"
            type="text"
            {...register("account_number")}
            error={!!errors.account_number?.message}
            errorMessage={errors.account_number?.message as string}
          />

          <TextField
            label="Routing number or Swift code"
            placeholder="Enter Swift code or Routing number"
            type="text"
            {...register("swift_code")}
            error={!!errors.swift_code?.message}
            errorMessage={errors.swift_code?.message as string}
          />

          <TextField
            label={`${
              isPersonalAccount ? "Recipient" : "Company"
            } account name`}
            placeholder={`Enter ${
              isPersonalAccount ? "Recipient" : "Company"
            } account name`}
            type="text"
            {...register(
              isPersonalAccount
                ? "recipient_account_name"
                : "company_account_name"
            )}
            error={
              isPersonalAccount
                ? !!errors.recipient_account_name?.message
                : !!errors.company_account_name?.message
            }
            errorMessage={
              (isPersonalAccount
                ? errors.recipient_account_name?.message
                : errors.company_account_name?.message) as string
            }
          />

          <TextField
            label="Bank name"
            placeholder="Enter bank name"
            type="text"
            {...register("bank_name")}
            error={!!errors.rootbank_name?.message}
            errorMessage={errors.bank_name?.message as string}
          />

          <TextAreaField
            rows={3}
            label="Bank address"
            placeholder="Enter bank address"
            {...register("bank_address")}
            error={!!errors.bank_address?.message}
            errorMessage={errors.bank_address?.message as string}
          />

          <TextAreaField
            rows={3}
            label={`${isPersonalAccount ? "Recipient" : "Company"} address`}
            placeholder={`Enter ${
              isPersonalAccount ? "Recipient" : "Company"
            } address`}
            {...register(
              isPersonalAccount ? "recipient_address" : "company_address"
            )}
            error={
              isPersonalAccount
                ? !!errors.recipient_address?.message
                : !!errors.company_address?.message
            }
            errorMessage={
              (isPersonalAccount
                ? errors.recipient_address?.message
                : errors.company_address?.message) as string
            }
          />

          {addIntermediary ? (
            <>
              <Text medium semiBold tw="mt-[12px]">
                Intermediary bank details
              </Text>

              <TextField
                label="Intermediary bank name"
                placeholder="Enter bank name"
                type="text"
                {...register("intermediary_bank_name")}
                error={!!errors.intermediary_bank_name?.message}
                errorMessage={errors.intermediary_bank_name?.message as string}
              />

              <TextAreaField
                rows={3}
                label="Intermediary bank address"
                placeholder="Enter bank address"
                {...register("intermediary_bank_address")}
                error={!!errors.intermediary_bank_address?.message}
                errorMessage={
                  errors.intermediary_bank_address?.message as string
                }
              />

              <TextField
                label="Routing number or Swift code"
                placeholder="Enter Swift code or Routing number"
                type="text"
                {...register("intermediary_swift_code")}
                error={!!errors.intermediary_swift_code?.message}
                errorMessage={errors.intermediary_swift_code?.message as string}
              />
            </>
          ) : (
            <button
              type="button"
              onClick={() => setAddIntermediary(true)}
              tw="flex gap-[4px] items-center"
            >
              <Plus />
              <Text tw="text-primary70">Add Intermediary details</Text>
            </button>
          )}
        </AccountDetails>

        <Button primary fullWidth type="submit" loading={isInitializing}>
          Save
        </Button>
      </Card>
      <EnableTwoStepAuthModal />
    </form>
  );
}

const AccountDetails = styled.div`
  ${tw`flex flex-col gap-[16px] mb-[24px] `}
`;
